import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Grid2 as Grid,
  Autocomplete,
  TextField,
  FormControlLabel,
  Checkbox,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import axios from "axios";
import ActiveFormTables from "./ActiveFormTables";
import DownloadTemplateDialog from "./DownloadTemplateDialog";
import PDFFormEditor from "../../../common/PDFFormEditor";
import { extractFormFields } from "../../../uploaderComponents/helpers/processPDF";

const ActiveForms = ({ onError, onSuccess, currentSession }) => {
  // State management
  const [forms, setForms] = useState([]);
  const [jurisdictions, setJurisdictions] = useState({
    countries: [],
    states: [],
    counties: [],
    cities: [],
  });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedCountry, setSelectedCountry] = useState("jur_us");
  const [selectedState, setSelectedState] = useState("jur_us_fl");
  const [selectedCounty, setSelectedCounty] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [deleteLoading, setDeleteLoading] = useState("");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [stateFormsOnly, setStateFormsOnly] = useState(false);
  const [countyFormsOnly, setCountyFormsOnly] = useState(false);
  const [nocFilter, setNocFilter] = useState("include"); // "include", "exclude", "only"
  const [sortField, setSortField] = useState("_id");
  const [sortDirection, setSortDirection] = useState("desc");
  const [downloadLoading, setDownloadLoading] = useState("");
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const [selectedFormForDownload, setSelectedFormForDownload] = useState(null);
  const [pdfEditorOpen, setPdfEditorOpen] = useState(false);
  const [selectedFormForEdit, setSelectedFormForEdit] = useState(null);
  const [formData, setFormData] = useState(null);

  // Use ref to avoid dependency issues with error handlers
  const onErrorRef = useRef(onError);
  const onSuccessRef = useRef(onSuccess);

  // Update refs when props change
  useEffect(() => {
    onErrorRef.current = onError;
    onSuccessRef.current = onSuccess;
  }, [onError, onSuccess]);

  // Fetch jurisdictions hierarchically
  const fetchJurisdictions = useCallback(
    async (countryId = null, stateId = null, countyId = null) => {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/jurisdictions/hierarchical`,
          {
            currentSession,
            selected_country: countryId,
            selected_state: stateId,
            selected_county: countyId,
          },
          { withCredentials: true }
        );

        if (response.data.success) {
          setJurisdictions({
            countries: response.data.data.countries || [],
            states: response.data.data.states || [],
            counties: response.data.data.counties || [],
            cities: response.data.data.cities || [],
          });
        }
      } catch (error) {
        console.error("Error fetching jurisdictions:", error);
      }
    },
    [currentSession]
  );

  // Fetch forms data with optimized dependency tracking
  const fetchForms = useCallback(async () => {
    try {
      setLoading(true);

      // Determine which jurisdiction to use for filtering based on level filters
      let jurisdictionFilter = null;
      let levelFilter = null;

      if (stateFormsOnly) {
        // State forms only - jurisdiction_id should match selected state and country
        jurisdictionFilter = selectedState;
        levelFilter = "state";
      } else if (countyFormsOnly) {
        // County forms only - jurisdiction_id should match selected county (level 2)
        jurisdictionFilter = selectedCounty || selectedState;
        levelFilter = "county";
      } else {
        // Default behavior - use the most specific jurisdiction selected
        if (selectedCity) {
          jurisdictionFilter = selectedCity; // Level 3 - City
        } else if (selectedCounty) {
          jurisdictionFilter = selectedCounty; // Level 2 - County
        } else if (selectedState) {
          jurisdictionFilter = selectedState; // Level 1 - State
        } else if (selectedCountry) {
          jurisdictionFilter = selectedCountry; // Level 0 - Country
        }
      }

      // Add request cancellation for better performance
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/forms/list`,
        {
          page: page + 1,
          limit: rowsPerPage,
          jurisdiction_id: jurisdictionFilter,
          level_filter: levelFilter,
          noc_filter: nocFilter,
          sort_field: sortField,
          sort_direction: sortDirection,
          currentSession: currentSession,
        },
        {
          withCredentials: true,
          signal: controller.signal,
          timeout: 30000, // 30 second timeout
        }
      );

      clearTimeout(timeoutId);

      if (response.data.success) {
        setForms(response.data.data.forms);
        setTotalCount(response.data.data.total);
      } else {
        onErrorRef.current("Failed to fetch forms");
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Error fetching forms:", error);
        onErrorRef.current("Error fetching forms");
      }
    } finally {
      setLoading(false);
    }
  }, [
    page,
    rowsPerPage,
    selectedCity,
    selectedCounty,
    selectedState,
    selectedCountry,
    stateFormsOnly,
    countyFormsOnly,
    nocFilter,
    sortField,
    sortDirection,
    currentSession,
  ]);

  useEffect(() => {
    fetchJurisdictions(); // Load all jurisdictions initially
  }, [fetchJurisdictions]);

  useEffect(() => {
    fetchForms();
  }, [fetchForms]);

  // Handle jurisdiction filter changes
  const handleCountryChange = (event) => {
    const newCountryId = event.target.value;
    setSelectedCountry(newCountryId);
    setSelectedState("");
    setSelectedCounty("");
    setSelectedCity("");
    setPage(0);
    // Fetch jurisdictions with new country selection
    fetchJurisdictions(newCountryId, null, null);
  };

  const handleStateChange = (event) => {
    const newStateId = event.target.value;
    setSelectedState(newStateId);
    setSelectedCounty("");
    setSelectedCity("");
    setPage(0);
    // Fetch jurisdictions with new state selection
    fetchJurisdictions(selectedCountry, newStateId, null);
  };

  const handleCountyChange = (event) => {
    const newCountyId = event.target.value;
    setSelectedCounty(newCountyId);
    setSelectedCity("");
    setPage(0);
    // Fetch jurisdictions with new county selection
    fetchJurisdictions(selectedCountry, selectedState, newCountyId);
  };

  const handleCityChange = (event) => {
    setSelectedCity(event.target.value);
    setPage(0);
  };

  // Handle level filter changes
  const handleStateFormsChange = (event) => {
    const checked = event.target.checked;
    setStateFormsOnly(checked);
    if (checked) {
      setCountyFormsOnly(false);
      setSelectedCounty("");
      setSelectedCity("");
    }
    setPage(0);
  };

  const handleCountyFormsChange = (event) => {
    const checked = event.target.checked;
    setCountyFormsOnly(checked);
    if (checked) {
      setStateFormsOnly(false);
      setSelectedCity("");
    }
    setPage(0);
  };

  // Handle NOC filter changes
  const handleNocFilterChange = (event) => {
    setNocFilter(event.target.value);
    setPage(0);
  };

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // New field, default to ascending
      setSortField(field);
      setSortDirection("asc");
    }
    setPage(0);
  };

  // Handle download button click - open dialog
  const handleDownloadClick = useCallback((formId) => {
    setSelectedFormForDownload({ formId });
    setDownloadDialogOpen(true);
  }, []);

  // Handle edit button click - open PDF editor
  const handleEditClick = useCallback(async (formId) => {
    try {
      setLoading(true);

      // Fetch form data including PDF and form fields
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/admin/forms/get-form-data`,
        { formId },
        { withCredentials: true }
      );

      if (response.data.success) {
        setSelectedFormForEdit(formId);
        // Normalize form fields from backend if present
        const data = response.data.data || {};
        const fields = Array.isArray(data.formFields) ? data.formFields : [];
        let normalized = fields.map((f) => ({
          ...f,
          x: Number(f.x || f.rect?.x || 0),
          y: Number(f.y || f.rect?.y || 0),
          width: Number(f.width || f.rect?.width || 0),
          height: Number(f.height || f.rect?.height || 0),
          page: Number(f.page || f.pageIndex || 1),
        }));

        // Convert Blob to File object like UploadBox does
        let pdfFile = null;
        try {
          const pdfRes = await axios.post(
            `${process.env.REACT_APP_BASE_URL}/nocApi/admin/forms/download-template`,
            { formId, downloadType: "pdf" },
            { withCredentials: true, responseType: "blob" }
          );
          const blob = pdfRes.data;
          // Convert Blob to File object
          pdfFile = new File([blob], `${formId}.pdf`, {
            type: "application/pdf",
          });
        } catch (e) {
          console.error("PDF fetch for render failed:", e);
        }

        // If no fields came from backend, extract from PDF (use File directly)
        if (!normalized.length && pdfFile) {
          try {
            const extracted = await extractFormFields(pdfFile);
            normalized = extracted || [];
          } catch (err) {
            console.error("Field extraction failed:", err);
          }
        }

        setFormData({
          ...data,
          pdfFile,
          base64File: undefined,
          formFields: normalized,
        });

        // Close side navigation only when PDF is ready to render
        window.dispatchEvent(new Event("closeSideNav"));
        window.dispatchEvent(new Event("forceHideSideNavOn"));
        window.dispatchEvent(new Event("forceHideAppChromeOn"));
        setPdfEditorOpen(true);
      } else {
        onErrorRef.current("Failed to load form data");
      }
    } catch (error) {
      console.error("Error loading form data:", error);
      onErrorRef.current("Error loading form data");
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle PDF editor close
  const handlePdfEditorClose = useCallback(() => {
    window.dispatchEvent(new Event("forceHideSideNavOff"));
    window.dispatchEvent(new Event("forceHideAppChromeOff"));
    setPdfEditorOpen(false);
    setSelectedFormForEdit(null);
    setFormData(null);
  }, []);

  // Handle PDF editor save
  const handlePdfEditorSave = useCallback(
    async (updatedFormData) => {
      try {
        setLoading(true);

        const response = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/admin/forms/save-form-data`,
          {
            formId: selectedFormForEdit,
            formData: updatedFormData,
          },
          { withCredentials: true }
        );

        if (response.data.success) {
          onSuccessRef.current("Form updated successfully");
          setPdfEditorOpen(false);
          setSelectedFormForEdit(null);
          setFormData(null);
          fetchForms(); // Refresh the forms list
        } else {
          onErrorRef.current("Failed to save form changes");
        }
      } catch (error) {
        console.error("Error saving form data:", error);
        onErrorRef.current("Error saving form changes");
      } finally {
        setLoading(false);
      }
    },
    [selectedFormForEdit, fetchForms]
  );

  // Handle actual download
  const handleDownload = useCallback(
    async (formId, downloadType) => {
      try {
        if (downloadLoading === formId) {
          return;
        }
        setDownloadLoading(formId);

        const response = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/admin/forms/download-template`,
          { formId, downloadType },
          {
            withCredentials: true,
            responseType: "blob",
          }
        );

        // Handle different response types
        if (
          downloadType === "image" &&
          response.headers["content-type"] === "application/zip"
        ) {
          // For images, we might get a zip file with multiple images
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `${formId}_images.zip`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        } else {
          // For PDF or single image
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `${formId}.pdf`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        }

        setDownloadDialogOpen(false);
        setSelectedFormForDownload(null);
      } catch (error) {
        console.error("Download error:", error);
        onErrorRef.current("Download failed");
      } finally {
        setDownloadLoading(false);
      }
    },
    [downloadLoading]
  );

  // Handle download dialog close
  const handleDownloadDialogClose = useCallback(() => {
    if (!downloadLoading) {
      setDownloadDialogOpen(false);
      setSelectedFormForDownload(null);
    }
  }, [downloadLoading]);

  // Handle delete confirmation
  const handleDeleteClick = useCallback((formId, fileName, blobName) => {
    setFileToDelete({ formId, fileName, blobName });
    setDeleteConfirmOpen(true);
  }, []);

  // Handle file deletion
  const handleDelete = useCallback(async () => {
    if (!fileToDelete || deleteLoading === fileToDelete.formId) {
      return;
    }

    try {
      setDeleteLoading(fileToDelete.formId);
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/admin/forms/delete`,
        {
          formId: fileToDelete.formId,
          fileName: fileToDelete.fileName,
          blobName: fileToDelete.blobName,
        },
        { withCredentials: true }
      );

      if (response.data.success) {
        onSuccessRef.current("Form deleted successfully");
        fetchForms(); // Refresh the list
        setDeleteConfirmOpen(false);
        setFileToDelete(null);
      } else {
        onErrorRef.current("Delete failed");
      }
    } catch (error) {
      console.error("Delete error:", error);
      onErrorRef.current("Delete failed");
    } finally {
      setDeleteLoading("");
    }
  }, [fileToDelete, deleteLoading, fetchForms]);

  // Handle delete confirmation cancel
  const handleDeleteCancel = useCallback(() => {
    setDeleteConfirmOpen(false);
    setFileToDelete(null);
  }, []);

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Memoize expensive computations
  const memoizedForms = useMemo(() => forms, [forms]);
  const memoizedJurisdictions = useMemo(() => jurisdictions, [jurisdictions]);

  // Memoize filter options to prevent unnecessary re-renders
  const countyOptions = useMemo(
    () => [{ id: "", name: "All Counties" }, ...jurisdictions.counties],
    [jurisdictions.counties]
  );

  const cityOptions = useMemo(
    () => [{ id: "", name: "All Cities" }, ...jurisdictions.cities],
    [jurisdictions.cities]
  );

  return (
    <Box>
      {/* Filters and Actions */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Autocomplete
              value={
                memoizedJurisdictions.countries.find(
                  (c) => c.id === selectedCountry
                ) || null
              }
              onChange={(event, newValue) => {
                handleCountryChange({ target: { value: newValue?.id || "" } });
              }}
              options={memoizedJurisdictions.countries}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Country"
                  variant="filled"
                  size="small"
                  sx={{
                    "& .MuiFilledInput-root": {
                      borderRadius: 2,
                      border: "2px solid",
                      borderColor: "primary.main",
                      backgroundColor: "white",
                      "&:hover": {
                        borderColor: "primary.main",
                      },
                      "&.Mui-focused": {
                        borderColor: "primary.main",
                      },
                    },
                  }}
                />
              )}
              slotProps={{
                listbox: {
                  style: {
                    maxHeight: 300,
                    overflowY: "auto",
                  },
                },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Autocomplete
              value={
                memoizedJurisdictions.states.find(
                  (s) => s.id === selectedState
                ) || null
              }
              onChange={(event, newValue) => {
                handleStateChange({ target: { value: newValue?.id || "" } });
              }}
              options={memoizedJurisdictions.states}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="State"
                  variant="filled"
                  size="small"
                  sx={{
                    "& .MuiFilledInput-root": {
                      backgroundColor: "white",
                      borderRadius: 2,
                      border: "2px solid",
                      borderColor: "primary.main",
                      "&:hover": {
                        borderColor: "primary.main",
                      },
                      "&.Mui-focused": {
                        borderColor: "primary.main",
                      },
                    },
                  }}
                />
              )}
              slotProps={{
                listbox: {
                  style: {
                    maxHeight: 300,
                    overflowY: "auto",
                  },
                },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Autocomplete
              value={
                selectedCounty
                  ? memoizedJurisdictions.counties.find(
                      (c) => c.id === selectedCounty
                    ) || null
                  : null
              }
              onChange={(event, newValue) => {
                handleCountyChange({ target: { value: newValue?.id || "" } });
              }}
              options={countyOptions}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="County"
                  variant="filled"
                  size="small"
                  sx={{
                    "& .MuiFilledInput-root": {
                      borderRadius: 2,
                      border: "2px solid",
                      borderColor: "primary.main",
                      backgroundColor: "white",
                      "&:hover": {
                        borderColor: "primary.main",
                      },
                      "&.Mui-focused": {
                        borderColor: "primary.main",
                      },
                    },
                  }}
                />
              )}
              slotProps={{
                listbox: {
                  style: {
                    maxHeight: 300,
                    overflowY: "auto",
                  },
                },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Autocomplete
              value={
                selectedCity
                  ? memoizedJurisdictions.cities.find(
                      (c) => c.id === selectedCity
                    ) || null
                  : null
              }
              onChange={(event, newValue) => {
                handleCityChange({ target: { value: newValue?.id || "" } });
              }}
              options={cityOptions}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="City"
                  variant="filled"
                  size="small"
                  sx={{
                    "& .MuiFilledInput-root": {
                      borderRadius: 2,
                      border: "2px solid",
                      borderColor: "primary.main",
                      backgroundColor: "white",
                      "&:hover": {
                        borderColor: "primary.main",
                      },
                      "&.Mui-focused": {
                        borderColor: "primary.main",
                      },
                    },
                  }}
                />
              )}
              slotProps={{
                listbox: {
                  style: {
                    maxHeight: 300,
                    overflowY: "auto",
                  },
                },
              }}
            />
          </Grid>
        </Grid>

        {/* Level Filters */}
        <Box
          sx={{
            mt: 2,
            display: "flex",
            gap: 2,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={stateFormsOnly}
                onChange={handleStateFormsChange}
                color="primary"
              />
            }
            label="State Forms"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={countyFormsOnly}
                onChange={handleCountyFormsChange}
                color="primary"
              />
            }
            label="County Forms"
          />

          {/* NOC Filter */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              value={nocFilter}
              onChange={handleNocFilterChange}
              sx={{
                backgroundColor: "white",
                borderRadius: 2,
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "primary.main",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "primary.main",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "primary.main",
                },
              }}
            >
              <MenuItem value="include">Include NOC</MenuItem>
              <MenuItem value="exclude">Exclude NOC</MenuItem>
              <MenuItem value="only">NOC Only</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Forms Table */}
      <ActiveFormTables
        forms={memoizedForms}
        loading={loading}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={totalCount}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        onDownload={handleDownloadClick}
        onEdit={handleEditClick}
        onDelete={handleDeleteClick}
        deleteLoading={deleteLoading}
        onSort={handleSort}
        sortField={sortField}
        sortDirection={sortDirection}
        downloadLoading={downloadLoading}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to delete the form "{fileToDelete?.fileName}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone. The file will be removed permanently.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDeleteCancel}
            disabled={deleteLoading === fileToDelete?.formId}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            disabled={deleteLoading === fileToDelete?.formId}
            startIcon={
              deleteLoading === fileToDelete?.formId ? (
                <CircularProgress size={16} />
              ) : (
                <DeleteIcon />
              )
            }
          >
            {deleteLoading === fileToDelete?.formId ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Download Template Dialog */}
      <DownloadTemplateDialog
        open={downloadDialogOpen}
        onClose={handleDownloadDialogClose}
        onDownload={handleDownload}
        formId={selectedFormForDownload?.formId}
        loading={downloadLoading === selectedFormForDownload?.formId}
      />

      {/* PDF Form Editor */}
      <PDFFormEditor
        formData={formData}
        onClose={handlePdfEditorClose}
        onSave={handlePdfEditorSave}
        isOpen={pdfEditorOpen}
      />
    </Box>
  );
};

export default ActiveForms;
